/// <reference types="google.accounts" />
import type { User } from '@iheartjane/data-access';
import { trackError } from '@iheartjane/tracking';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import snakeCase from 'lodash-es/snakeCase';
import type { NavigateFunction } from 'react-router';
import type { DeepReadonly } from 'ts-essentials';

import type { CustomerThunkAction } from '@jane/monolith/customer/customerAction';
import { CustomerSource } from '@jane/monolith/sources/customer';
import { UsersSource } from '@jane/monolith/sources/users';
import type { SSOChannel } from '@jane/shared-ecomm/tracking';
import {
  trackCustomerLogin,
  trackCustomerRegistration,
} from '@jane/shared-ecomm/tracking';
import { removeJwt, setJwt } from '@jane/shared/auth';
import type { AppleAuthResponse } from '@jane/shared/hooks';
/* eslint-disable-next-line no-restricted-imports */
import type { LoginCredentials } from '@jane/shared/models';
import { isEmbeddedMode, monolithPaths as paths } from '@jane/shared/util';

import { getCartForUser, removeCartUser } from './cart';
import { verifyStoreSuccess } from './embeddedApp';
import { updateIdentificationSuccess } from './identification';
import { closeModal, openModal } from './modal';
import { updateUsersSuccess } from './users';

export const handleInvalidLoginToken =
  (redirectToLogin = false, navigate?: NavigateFunction): CustomerThunkAction =>
  (dispatch, _, extraArgs) => {
    const appMode = extraArgs?.current?.appMode;
    const isEmbed = isEmbeddedMode(appMode);
    const showLoginModal = !isEmbed && !redirectToLogin;
    removeJwt();
    if (showLoginModal) {
      dispatch(closeModal());
    }
    dispatch(handleInvalidLoginTokenAction());

    if (showLoginModal) {
      dispatch(openModal({ name: 'login' }));
    } else if (isEmbed) {
      navigate && navigate(paths.embeddedLogin());
    }
  };

export const logOut =
  (onSuccess?: () => void): CustomerThunkAction =>
  (dispatch) => {
    CustomerSource.logOut()
      .then(() => removeJwt())
      .then(() => {
        dispatch(customerLogOutSuccess());
        dispatch(removeCartUser());
        onSuccess && onSuccess();
      });
  };

export const setSignedInUser =
  ({ user, jwt }: { jwt: string; user: User }): CustomerThunkAction =>
  (dispatch) => {
    if (!jwt) {
      return;
    }
    setJwt(`Bearer ${jwt}`);
    dispatch(setSignedInUserSuccess(user));
  };

export const logIn =
  ({
    credentials,
    nextPathname,
    navigate,
    source,
  }: {
    credentials: LoginCredentials;
    navigate: NavigateFunction;
    nextPathname: string | null;
    source?: string;
  }): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateCustomerLogIn());

    CustomerSource.logIn(credentials).then((response) => {
      const { body, headers } = response;
      const { user, errors } = body;

      if (errors) {
        return dispatch(logInError(errors.error));
      }

      return dispatch(
        logInSuccess({ user, headers, nextPathname, navigate, source })
      );
    });
  };

export const logInWithCognitoToken =
  (token: string): CustomerThunkAction<Promise<unknown>> =>
  (dispatch) => {
    dispatch(initiateCustomerLogIn());

    return CustomerSource.logInWithCognitoToken(token).then((response) => {
      const { body, headers } = response;
      const { user, error } = body;

      if (body.status === 404) {
        return dispatch(logInError(error));
      }

      // handle expired tokens
      if (!body.status && body.errors) {
        return dispatch(logInError(body.errors.error));
      }

      return dispatch(headlessLogInSuccess({ user, headers }));
    });
  };

export const logInSuccess =
  ({
    user,
    headers,
    nextPathname,
    source,
    ssoChannel,
    navigate,
  }: {
    headers: Record<string, string | null>;
    navigate?: NavigateFunction | false;
    nextPathname: string | null;
    source?: string;
    ssoChannel?: SSOChannel;
    user: User;
  }): CustomerThunkAction =>
  (dispatch, _, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const isEmbed = isEmbeddedMode(appMode);
    if (headers && headers['Authorization']) {
      setJwt(headers['Authorization']);
    }
    trackCustomerLogin({ ssoChannel, source: source! });
    dispatch(logInSucceeded(user));

    dispatch(getCartForUser());

    const path =
      nextPathname && !nextPathname.includes('logout')
        ? nextPathname
        : isEmbed
          ? paths.embeddedMenu()
          : paths.root();

    if (navigate) {
      navigate(path, { replace: true });
    }
  };

const headlessLogInSuccess =
  ({
    headers,
    user,
  }: {
    headers: Record<string, string | null>;
    user: User;
  }): CustomerThunkAction =>
  (dispatch) => {
    if (headers && headers['Authorization']) {
      setJwt(headers['Authorization'], true);
    }
    trackCustomerLogin({ ssoChannel: 'cognito', source: 'headless' });
    dispatch(headlessLogInSucceeded(user));
  };

export const registerSuccess =
  (user: User): CustomerThunkAction =>
  (dispatch) => {
    dispatch(getCartForUser());
    dispatch(customerRegisterSuccess(user));
  };

export const whoami =
  ({ ref }: { ref?: string } = {}): CustomerThunkAction =>
  (dispatch) => {
    dispatch(initiateWhoami());

    CustomerSource.whoami({ ref }).then(
      (response) => {
        const { user } = response;
        return dispatch(whoamiSuccess(user));
      },
      (err) => dispatch(whoamiError(err))
    );
  };

export const authenticateGoogleCode =
  ({
    code,
    nextPathname = null,
    onAuthSuccess,
    navigate,
    source,
  }: {
    code: google.accounts.oauth2.CodeResponse;
    navigate: NavigateFunction | false;
    nextPathname: string | null;
    onAuthSuccess?: () => void;
    source?: string;
  }): CustomerThunkAction =>
  (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;

    const state = getState();
    const { partnerId, partnerName } = state.embeddedApp;
    const { store } = state.store;
    const storeId = store?.id;

    CustomerSource.authenticateGoogleCode(code)
      .then((response) => {
        const { body, headers } = response;
        // track new oauth identity linked to existing account:
        const { user, errors } = body;

        if (!user || errors) {
          return dispatch(logInError(errors.error));
        }

        user.authenticated = true;

        dispatch(
          logInSuccess({
            user,
            headers,
            nextPathname,
            source,
            ssoChannel: 'google',
            navigate,
          })
        );

        if (user && user.new_registration) {
          UsersSource.update({
            user: {
              app_mode_at_registration: snakeCase(appMode),
              store_id_at_registration: storeId,
            },
          }).catch((error) => {
            trackError(new Error('User update failed'), {
              error,
            });
          });

          trackCustomerRegistration({
            customer: { ...user, ssoChannel: 'google' },
            partnerChannel: appMode,
            partnerId,
            partnerName,
          });
        }

        onAuthSuccess && onAuthSuccess();
        return undefined;
      })
      .catch((error) =>
        trackError(new Error('Google authentication failed'), {
          error,
        })
      );
  };

interface AppleAuthParams {
  first_name?: string;
  id_token: string;
  last_name?: string;
}

export const authenticateAppleCode =
  ({
    authMetadata,
    nextPathname = null,
    onAuthSuccess,
    navigate,
    source,
  }: {
    authMetadata: AppleAuthResponse;
    navigate: NavigateFunction | false;
    nextPathname: string | null;
    onAuthSuccess?: () => void;
    source?: string;
  }): CustomerThunkAction =>
  (dispatch, getState, extraArgsRef) => {
    const appMode = extraArgsRef?.current?.appMode;
    const auth: AppleAuthParams = {
      id_token: authMetadata.authorization.id_token,
    };

    if (authMetadata.user) {
      auth.first_name = authMetadata.user?.name?.firstName;
      auth.last_name = authMetadata.user?.name?.lastName;
    }

    const state = getState();
    const { partnerId, partnerName } = state.embeddedApp;
    const { store } = state.store;
    const storeId = store?.id;

    CustomerSource.authenticateAppleCode(auth)
      .then((response) => {
        const { body, headers } = response;
        // track new oauth identity linked to existing account:
        const { user, errors } = body;

        if (!user || errors) {
          return dispatch(logInError(errors.error));
        }

        user.authenticated = true;

        dispatch(
          logInSuccess({
            user,
            headers,
            nextPathname,
            source,
            ssoChannel: 'apple',
            navigate,
          })
        );

        if (user && user.new_registration) {
          UsersSource.update({
            user: {
              app_mode_at_registration: snakeCase(appMode),
              store_id_at_registration: storeId,
            },
          }).catch((error) => {
            trackError(new Error('User update failed'), {
              error,
            });
          });

          trackCustomerRegistration({
            customer: { ...user, ssoChannel: 'apple' },
            partnerChannel: appMode,
            partnerId,
            partnerName,
          });
        }

        onAuthSuccess && onAuthSuccess();
        return undefined;
      })
      .catch((error) =>
        trackError(new Error('Apple authentication failed'), {
          error,
        })
      );
  };

export type CustomerReducerState = DeepReadonly<{
  authenticated: boolean;
  avatar: number | null;
  birth_date: string;
  email: string | null;
  externalId: string | null;
  firstName: string | null;
  id: number | null;
  isEmbeddedMode: boolean;
  lastMfa: Date | null;
  lastName: string | null;
  loading: boolean;
  logInErrorMessage: string | null;
  name: string | null;
  nickname: string | null;
  phone: string | null;
  promotionsOptOut: boolean;
  sessionChecked: boolean;
}>;

const getInitialState = (): CustomerReducerState => ({
  isEmbeddedMode: false,
  sessionChecked: false,
  loading: false,
  logInErrorMessage: null,
  authenticated: false,
  avatar: null,
  birth_date: '',
  email: null,
  externalId: null,
  firstName: null,
  id: null,
  lastMfa: null,
  lastName: null,
  name: null,
  nickname: null,
  phone: null,
  promotionsOptOut: false,
});

const setUser = (state: CustomerReducerState, user: User) => ({
  ...state,
  authenticated: user.authenticated || false,
  avatar: user.avatar,
  birth_date: user['birth_date'] || '',
  email: user.email || '',
  // Preserve a previously-set externalId (e.g. a headless partner's externalId
  // captured at cart build) when the authenticated Jane user record doesn't
  // carry one. Consistent with reset(), which also leaves externalId sticky.
  externalId: user.externalId || state.externalId,
  firstName: user['first_name'] || '',
  id: user.id,
  lastName: user['last_name'],
  name: user.name,
  nickname: user.nickname,
  phone: user.phone,
  promotionsOptOut: user['promotions_opt_out'],
});

const setUserFromPartialIdentification = (
  state: CustomerReducerState,
  user: Partial<User>
) => {
  const firstName =
    typeof user.first_name === 'string' ? user.first_name : state.firstName;
  const lastName =
    typeof user.last_name === 'string' ? user.last_name : state.lastName;
  const name = [firstName, lastName].join(' ');

  return {
    ...state,
    firstName,
    lastName,
    name,
  };
};

const reset = (state: CustomerReducerState) => ({
  ...state,
  loading: false,
  authenticated: false,
  avatar: null,
  birth_date: '',
  email: null,
  firstName: null,
  id: null,
  lastName: null,
  name: null,
  nickname: null,
  phone: null,
  promotionsOptOut: false,
});

const applyLoggedInUser = (
  state: CustomerReducerState,
  action: PayloadAction<User>
) =>
  setUser(
    { ...state, loading: false, logInErrorMessage: null },
    action.payload
  );

const customerSlice = createSlice({
  name: 'customer',
  initialState: getInitialState(),
  reducers: {
    handleInvalidLoginTokenAction: (state) => {
      return state.id ? { ...state, authenticated: false } : state;
    },
    initiateCustomerLogIn: (state) => {
      return { ...state, loading: true };
    },
    initiateWhoami: (state) => {
      return { ...state, loading: true };
    },
    logInError: (state, action: PayloadAction<string | null>) => {
      return { ...state, loading: false, logInErrorMessage: action.payload };
    },
    registerError: (state, action: PayloadAction<string | null>) => {
      return { ...state, loading: false, logInErrorMessage: action.payload };
    },
    setLocalUser: (state, action: PayloadAction<Partial<User> & object>) => {
      return {
        ...state,
        phone: action.payload.phone || null,
        email: action.payload.email || null,
        firstName: action.payload.first_name || null,
        lastName: action.payload.last_name || null,
        birth_date: action.payload.birth_date || '',
        externalId: action.payload.externalId || null,
        promotionsOptOut: !!action.payload.promotions_opt_out,
      };
    },
    headlessLogInSucceeded: applyLoggedInUser,
    logInSucceeded: applyLoggedInUser,
    customerRegisterSuccess: applyLoggedInUser,
    setSignedInUserSuccess: applyLoggedInUser,
    customerLogOutSuccess: reset,
    whoamiError: reset,
    whoamiSuccess: (state, action: PayloadAction<User>) => {
      return setUser(
        { ...state, loading: false, sessionChecked: true },
        action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateIdentificationSuccess, (state, action) => {
      return setUserFromPartialIdentification(state, action.payload);
    });
    builder.addCase(updateUsersSuccess, (state, action) => {
      return setUser(state, action.payload);
    });
    builder.addCase(verifyStoreSuccess, (state) => {
      return { ...state, isEmbeddedMode: true };
    });
  },
  selectors: {
    selectCustomer: (state) => state,
  },
});

export const {
  actions: {
    customerLogOutSuccess,
    customerRegisterSuccess,
    headlessLogInSucceeded,
    initiateCustomerLogIn,
    initiateWhoami,
    handleInvalidLoginTokenAction,
    logInError,
    logInSucceeded,
    registerError,
    setLocalUser,
    setSignedInUserSuccess,
    whoamiError,
    whoamiSuccess,
  },
  reducer: customerReducer,
  selectors: { selectCustomer },
} = customerSlice;

export type CustomerActions =
  | ReturnType<typeof customerLogOutSuccess>
  | ReturnType<typeof initiateCustomerLogIn>
  | ReturnType<typeof logInError>
  | ReturnType<typeof customerRegisterSuccess>
  | ReturnType<typeof registerError>
  | ReturnType<typeof initiateWhoami>
  | ReturnType<typeof whoamiSuccess>
  | ReturnType<typeof whoamiError>
  | ReturnType<typeof logInSucceeded>
  | ReturnType<typeof setSignedInUserSuccess>
  | ReturnType<typeof headlessLogInSucceeded>
  | ReturnType<typeof setLocalUser>
  | ReturnType<typeof handleInvalidLoginTokenAction>;
